// Admin Course Settings — manage the configurable dropdown lists used by the Course form:
// Category, Personas, Region, Zone, Market.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, SlidersHorizontal } from 'lucide-react';

const TYPES = [
    { key: 'category', label: 'Categories', hint: 'Course category options' },
    { key: 'persona', label: 'Personas', hint: 'Who a course is targeted to' },
    { key: 'region', label: 'Regions', hint: 'Geographic region options' },
    { key: 'zone', label: 'Zones', hint: 'Zone options' },
    { key: 'market', label: 'Markets', hint: 'Market segment options' },
];

const inputCls = 'flex-1 px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:ring-2 focus:ring-amber-400 focus:outline-none text-sm bg-white';

export default function AdminTaxonomies() {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [drafts, setDrafts] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/taxonomies');
            setData(r.data || {});
        } catch { toast.error('Failed to load lists'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const add = async (type) => {
        const value = (drafts[type] || '').trim();
        if (!value) { toast.error('Enter a value'); return; }
        try {
            await api.post('/admin/taxonomies', { type, value });
            setDrafts((d) => ({ ...d, [type]: '' }));
            toast.success('Added');
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || 'Add failed'); }
    };

    const remove = async (id) => {
        try { await api.delete(`/admin/taxonomies/${id}`); load(); }
        catch { toast.error('Delete failed'); }
    };

    return (
        <div className="space-y-6" data-testid="admin-taxonomies-page">
            <div>
                <h1 className="font-display font-extrabold text-2xl text-slate-900 flex items-center gap-2">
                    <SlidersHorizontal className="h-6 w-6 text-amber-500" /> Course Settings
                </h1>
                <p className="text-sm text-slate-500 mt-1">Manage the dropdown options shown in the Add / Edit Course form.</p>
            </div>

            {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {TYPES.map((t) => (
                    <div key={t.key} className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5" data-testid={`tax-card-${t.key}`}>
                        <div className="font-bold text-slate-800">{t.label}</div>
                        <div className="text-xs text-slate-400 mb-3">{t.hint}</div>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {(data[t.key] || []).map((o) => (
                                <span key={o.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-bold" data-testid={`tax-item-${o.id}`}>
                                    {o.label}
                                    <button onClick={() => remove(o.id)} className="hover:text-rose-600" data-testid={`tax-del-${o.id}`}><Trash2 className="w-3 h-3" /></button>
                                </span>
                            ))}
                            {(data[t.key] || []).length === 0 && <span className="text-xs text-slate-400">No options yet</span>}
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                value={drafts[t.key] || ''}
                                onChange={(e) => setDrafts((d) => ({ ...d, [t.key]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(t.key); } }}
                                placeholder={`Add ${t.label.slice(0, -1).toLowerCase()}…`}
                                className={inputCls} data-testid={`tax-input-${t.key}`} />
                            <button onClick={() => add(t.key)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm" data-testid={`tax-add-${t.key}`}>
                                <Plus className="w-4 h-4" /> Add
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
